import React from 'react'
import { useGameStore } from '../../state/gameStore'
import { appBus } from '../../../app/app-events'
import { RefreshCw, Play, Box } from 'lucide-react'

const StatusBar: React.FC = () => {
  const { stats, cutsLimit } = useGameStore((state) => state.game)
  const { phase, commandLocked } = useGameStore((state) => state.ui.bnbState)
  const { cutsLimitCallback } = useGameStore((state) => state)

  const handleCutsConfig = () => {
    const newLimit = prompt('Set max cuts (1-50):', cutsLimit.toString())
    if (!newLimit || !cutsLimitCallback) return
    const parsed = parseInt(newLimit, 10)
    if (!isNaN(parsed) && parsed > 0 && parsed <= 50) cutsLimitCallback(parsed)
  }

  const handleStartBnB = () => {
    if (commandLocked) return
    appBus.emit('bnb-start-requested', undefined)
  }

  return (
    <div className="h-12 bg-slate-900/95 border-b border-white/10 flex items-center px-4 justify-between backdrop-blur-md">
      <div className="flex items-center gap-6">
        <div className="font-bold text-slate-100 tracking-tight flex items-center gap-2">
          <div className="w-3 h-3 bg-sky-500 rounded-sm rotate-45 shadow-[0_0_10px_rgba(14,165,233,0.5)]" />
          Sculptor
        </div>

        <div className="h-4 w-px bg-white/10" />

        <div className="flex items-center gap-4 text-xs font-medium font-mono">
          <div className="flex items-center gap-2 group cursor-pointer" onClick={handleCutsConfig} title="Cuts Remaining">
            <ScissorsIcon className="text-amber-500 group-hover:text-amber-400" />
            <span className="text-slate-300 group-hover:text-white">{stats.cutsRemaining} / {cutsLimit}</span>
          </div>
          <div className="flex items-center gap-2" title="Volume Reduction">
            <Box className="text-slate-500" size={14} />
            <span className="text-sky-400">{stats.volumeReduction.toFixed(1)}%</span>
          </div>
          <div className="flex items-center gap-2" title="Score">
            <TrophyIcon className="text-slate-500" />
            <span className="text-amber-400">{stats.currentScore}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => appBus.emit('refresh-cuts', {})}
          className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 text-slate-400 hover:text-white rounded transition-colors text-xs font-medium"
        >
          <RefreshCw size={14} />
          <span>Refresh</span>
        </button>

        <button
          onClick={handleStartBnB}
          disabled={commandLocked || phase === 'active' || phase === 'selecting' || phase === 'branching' || phase === 'backtracking'}
          className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors text-xs font-bold shadow-sm shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play size={14} fill="currentColor" />
          <span>{phase === 'finished' ? 'BnB Finished' : 'Branch & Bound'}</span>
        </button>
      </div>
    </div>
  )
}

const ScissorsIcon = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="6" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <line x1="20" y1="4" x2="8.12" y2="15.88" />
    <line x1="14.47" y1="14.48" x2="20" y2="20" />
    <line x1="8.12" y1="8.12" x2="12" y2="12" />
  </svg>
)

const TrophyIcon = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M8 21h8m-4-9v9m-8-3h16" />
    <path d="M12 17l-5.5-3a6 6 0 0 1 0-12h11a6 6 0 0 1 0 12z" />
  </svg>
)

export default StatusBar
